import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../services/modal.service';

import { forkJoin, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { I18nTranslator } from '../../i18n/translate';
import { Country, Language } from '../../models/lists.model';
import {
  Organisation,
  toAuthorizationsPayload,
  toCustomizationPayload,
  toGeneralPayload,
  toNotificationsPayload,
} from '../../models/organisation.model';
import {
  ADVICE_RETURN_TYPES_BLE,
  ADVICE_RETURN_TYPES_COLZA,
  ADVICE_RETURN_TYPES_ORGE,
  AdviceReturnType,
  fmsCanReturnAdvice,
  fmsHasImportSettings,
  fmsHasWiuzOptions,
  OrganisationFmsSettings,
  toFmsPayload,
} from '../../models/organisation-fms.model';
import { User } from '../../models/user.model';
import { ListsService } from '../../services/lists.service';
import { OrganisationService } from '../../services/organisation.service';
import { UserService } from '../../services/user.service';
import { hasPermission, showErrorMessage, sortingString, toBoolean } from '../../utils/utils';
import { TranslatePipe, TranslateWithPunctuationPipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { CollapsibleSectionComponent } from '../../components/collapsible-section/collapsible-section.component';
import {
  canEditOrganisationField,
  isOrganisationAdmin,
  isOrganisationFieldHidden,
  OrganisationField,
} from './organisation-field-access';

@Component({
  standalone: true,
  selector: 'organisation-edit',
  templateUrl: 'organisation-edit.component.html',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    TranslatePipe,
    TranslateWithPunctuationPipe,
    CommonModule,
    FormsModule,
    CollapsibleSectionComponent,
  ],
  providers: [OrganisationService, UserService],
})
export class OrganisationEditComponent implements OnInit, OnDestroy {
  @Input() organisation: Organisation;
  @Output() close = new EventEmitter();
  error: any;
  sub: any;
  edited = false;
  countries: Country[];
  languagesIndex: Map<number, Language>;

  organisationId: number;

  sectorOwners: User[];
  negoceOwners: User[];

  showManagers = false;
  managers: User[];

  // Block 3 of the organisation form (C2027-198): FMS (data entry tools).
  fmsSettings: OrganisationFmsSettings[] = [];
  advisors: User[] = [];
  readonly bleReturnTypes: readonly AdviceReturnType[] = ADVICE_RETURN_TYPES_BLE;
  readonly orgeReturnTypes: readonly AdviceReturnType[] = ADVICE_RETURN_TYPES_ORGE;
  readonly colzaReturnTypes: readonly AdviceReturnType[] = ADVICE_RETURN_TYPES_COLZA;
  readonly fmsHasImportSettings = fmsHasImportSettings;
  readonly fmsHasWiuzOptions = fmsHasWiuzOptions;
  readonly fmsCanReturnAdvice = fmsCanReturnAdvice;
  readonly trackByFms = (_: number, setting: OrganisationFmsSettings) => setting.fms;

  country: Country;

  @ViewChild('generalForm') generalForm: NgForm;
  @ViewChild('customizationForm') customizationForm: NgForm;
  @ViewChild('notificationsForm') notificationsForm: NgForm;
  @ViewChild('authorizationsForm') authorizationsForm: NgForm;
  @ViewChild('fmsForm') fmsForm: NgForm;

  savingBlock = false;
  savedBlock: 'general' | 'customization' | 'notifications' | 'authorizations' | 'fms' | null = null;
  // Set by onChangeCountry (editing only) when the chosen country differs from the stored one:
  // the languages must then be chosen in block 2 and saved together with block 1.
  countryChanged = false;
  customizationOpen = true;
  private storedCountryId: number;
  private savedBlockTimeout: any;
  // getOrganisation() and the countries/languages forkJoin race: whichever resolves first defers
  // the initial onChangeCountry() call to the other, so it never runs against an unset
  // this.countries (would throw and leave block 2's language selects permanently unrendered).
  private pendingCountryId: number = null;

  // SECTOR counts as admin for the fields reserved to it (C2027-194, PO Q1)
  isOrgAdmin = isOrganisationAdmin();
  readonly canEdit = (field: OrganisationField) => canEditOrganisationField(field);
  readonly isHidden = (field: OrganisationField) => isOrganisationFieldHidden(field);
  readonly userName = (user: User) =>
    user && user.id ? [user.firstName, user.lastName].filter(Boolean).join(' ') : '';

  // Keeps the <option> elements across a country switch so the <select>'s displayed value
  // stays in sync with the model: without it, Angular recreates every <option> (languagesGui
  // is a different Language[] instance per country) and the browser silently re-selects the
  // first one, even though organisation.defaultLanguageGui/Pdf did not change.
  readonly trackById = (_: number, lang: Language) => lang.id;

  constructor(
    private organisationService: OrganisationService,
    private listService: ListsService,
    private userService: UserService,
    private i18nTranslator: I18nTranslator,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  /** Roles allowed on this page: négoce owners and managers edit, they do not create. */
  static allowedRoles(editing: boolean): string[] {
    return editing ? ['ROLE_ADMIN', 'ROLE_SECTOR', 'ROLE_NEGOCE', 'ROLE_MANAGER'] : ['ROLE_ADMIN', 'ROLE_SECTOR'];
  }

  goBack(savedOrganisation: Organisation = null) {
    this.close.emit(savedOrganisation);
    this.router.navigate(['/organisations']);
    return false;
  }

  handleCountries(countries: Country[]) {
    this.countries = countries.sort(sortingString('label'));
    // France en haut
    const fr = this.countries.find((c) => c.code === 'FR');
    if (fr) {
      this.countries.splice(this.countries.indexOf(fr), 1);
      this.countries.unshift(fr);
    }
  }

  handleLanguages(languages: Language[]) {
    languages = languages.sort(sortingString('label'));
    this.languagesIndex = new Map<number, Language>();
    for (const language of languages) {
      this.languagesIndex.set(language.id, language);
    }
  }

  getManagers() {
    // TODO handle error
    this.userService.getUsers(this.organisationId, ['ROLE_MANAGER']).subscribe((m) => {
      this.managers = m;
      this.showManagers = true;
    });
  }

  getSectorOwners() {
    // TODO handle error
    this.userService.getUsers(null, ['ROLE_SECTOR', 'ROLE_ADMIN']).subscribe((users) => {
      this.sectorOwners = users;
      if (!this.edited && this.sectorOwners && this.sectorOwners.length === 1) {
        this.organisation.sectorOwner = this.sectorOwners[0];
      }
    });
  }

  getNegoceOwners() {
    // TODO handle error
    this.userService.getUsers(null, ['ROLE_NEGOCE']).subscribe((users) => {
      this.negoceOwners = users;
    });
  }

  save() {
    // Only an admin can set hasBeApi (C2027-194); creation is admin-only anyway, this is defensive.
    this.organisation.hasBeApi = this.isOrgAdmin ? toBoolean(this.organisation.hasBeApi) : false;
    this.organisation.hasI18n = toBoolean(this.organisation.hasI18n);

    // Build payload without mutating the form-bound model. Sending
    // {id: null} for an optional FK trips Hibernate's transient instance
    // check, so we omit the FK entirely when no id is selected.
    const payload: Organisation = { ...this.organisation };
    if (!payload.negoceOwner || !payload.negoceOwner.id) {
      payload.negoceOwner = null;
    }

    this.organisationService.save(payload).subscribe(
      (organisation) => {
        this.organisation = organisation;
        this.goBack(organisation);
      },
      (error) => this.handleSaveError(error),
    );
  }

  saveGeneral() {
    this.savingBlock = true;
    const payload = toGeneralPayload(this.organisation, this.countryChanged);
    this.organisationService
      .updateGeneral(this.organisation.id, payload)
      .pipe(finalize(() => (this.savingBlock = false)))
      .subscribe(
        () => {
          this.generalForm.form.markAsPristine();
          if (this.countryChanged) {
            // The server saved the country and the languages together: both blocks are now in sync.
            this.customizationForm.form.markAsPristine();
            this.countryChanged = false;
          }
          this.markBlockSaved('general');
        },
        (error) => this.handleSaveError(error),
      );
  }

  saveCustomization() {
    this.savingBlock = true;
    const payload = toCustomizationPayload(this.organisation);
    this.organisationService
      .updateCustomization(this.organisation.id, payload)
      .pipe(finalize(() => (this.savingBlock = false)))
      .subscribe(
        () => {
          this.customizationForm.form.markAsPristine();
          this.markBlockSaved('customization');
        },
        (error) => this.handleSaveError(error),
      );
  }

  saveNotifications() {
    this.savingBlock = true;
    const payload = toNotificationsPayload(this.organisation);
    this.organisationService
      .updateNotifications(this.organisation.id, payload)
      .pipe(finalize(() => (this.savingBlock = false)))
      .subscribe(
        () => {
          this.notificationsForm.form.markAsPristine();
          this.markBlockSaved('notifications');
        },
        (error) => this.handleSaveError(error),
      );
  }

  saveAuthorizations() {
    this.savingBlock = true;
    const payload = toAuthorizationsPayload(this.organisation);
    this.organisationService
      .updateAuthorizations(this.organisation.id, payload)
      .pipe(finalize(() => (this.savingBlock = false)))
      .subscribe(
        () => {
          this.authorizationsForm.form.markAsPristine();
          this.markBlockSaved('authorizations');
        },
        (error) => this.handleSaveError(error),
      );
  }

  saveFms() {
    this.savingBlock = true;
    const payload = toFmsPayload(this.fmsSettings, this.organisation.hasBeApi);
    this.organisationService
      .updateFms(this.organisation.id, payload)
      .pipe(finalize(() => (this.savingBlock = false)))
      .subscribe(
        (form) => {
          this.fmsSettings = form.fms;
          this.fmsForm.form.markAsPristine();
          this.markBlockSaved('fms');
        },
        (error) => this.handleSaveError(error),
      );
  }

  getAdvisors() {
    this.userService.getUsers(this.organisationId, ['ROLE_ADVISOR']).subscribe((advisors) => {
      this.advisors = advisors;
    });
  }

  advisorInList(advisorId: number): boolean {
    return !!this.advisors && this.advisors.some((a) => a.id === +advisorId);
  }

  isReturnTypeChecked(setting: OrganisationFmsSettings, type: AdviceReturnType): boolean {
    return !setting.disabledReturnTypes.includes(type);
  }

  toggleReturnType(setting: OrganisationFmsSettings, type: AdviceReturnType, returned: boolean) {
    if (returned) {
      setting.disabledReturnTypes = setting.disabledReturnTypes.filter((t) => t !== type);
    } else if (!setting.disabledReturnTypes.includes(type)) {
      setting.disabledReturnTypes = [...setting.disabledReturnTypes, type];
    }
    this.fmsForm.form.markAsDirty();
  }

  // Impact modals (PO Q49/Q10): the change only applies to the model if confirmed. On cancel,
  // setting.updateAllowed/defaultAdvisor never actually changed (ngModel here is one-way), so
  // writing back the same `previous` value is a no-op for NgModel's dirty-check and would leave
  // the radio/select showing the cancelled selection, because the browser's native click/select
  // already flipped the control's DOM state independently of Angular. Passing through the new
  // value first (in its own change-detection tick, via revertOneWayControl) forces NgModel to
  // register a real change on the following tick and re-sync the control back.
  private revertOneWayControl(apply: () => void) {
    // A macrotask, not a microtask: chaining .then() would run in the same zone turn as the
    // confirm() callback, before change detection ever runs, so NgModel would see only the final
    // value and skip the write (same bug this works around). setTimeout(0) forces a separate turn.
    setTimeout(apply, 0);
  }

  onUpdateAllowedChange(setting: OrganisationFmsSettings, newValue: boolean) {
    const previous = setting.updateAllowed;
    this.modal.confirm(this.i18nTranslator.translate('distributeur.edit.fms.updateChannel.confirm')).then((ok) => {
      if (ok) {
        setting.updateAllowed = newValue;
        this.fmsForm.form.markAsDirty();
        return;
      }
      setting.updateAllowed = newValue;
      this.revertOneWayControl(() => (setting.updateAllowed = previous));
    });
  }

  onDefaultAdvisorChange(setting: OrganisationFmsSettings, newAdvisorId: number | null) {
    const previous = setting.defaultAdvisor;
    const resolveAdvisor = (): OrganisationFmsSettings['defaultAdvisor'] => {
      if (newAdvisorId == null) {
        return null;
      }
      const advisor = this.advisors.find((a) => a.id === +newAdvisorId);
      return advisor || ({ id: +newAdvisorId } as any);
    };

    this.modal.confirm(this.i18nTranslator.translate('distributeur.edit.fms.defaultAdvisor.confirm')).then((ok) => {
      if (ok) {
        setting.defaultAdvisor = resolveAdvisor();
        this.fmsForm.form.markAsDirty();
        return;
      }
      setting.defaultAdvisor = resolveAdvisor();
      this.revertOneWayControl(() => (setting.defaultAdvisor = previous));
    });
  }

  private markBlockSaved(block: 'general' | 'customization' | 'notifications' | 'authorizations' | 'fms') {
    this.savedBlock = block;
    if (this.savedBlockTimeout) {
      clearTimeout(this.savedBlockTimeout);
    }
    this.savedBlockTimeout = setTimeout(() => (this.savedBlock = null), 3000);
  }

  ngOnInit() {
    this.sub = this.route.params.subscribe((params) => {
      const editing = this.route.snapshot.url[1].path === 'edit';
      if (hasPermission(OrganisationEditComponent.allowedRoles(editing), ['/home'], this.router)) {
        if (this.route.snapshot.url[1].path === 'edit') {
          this.organisationId = +params['organisationId'];
          this.edited = true;
          this.customizationOpen = false;
          this.organisationService.getOrganisation(this.organisationId).subscribe(
            (organisation) => {
              this.organisation = organisation;
              this.storedCountryId = organisation.country ? organisation.country.id : null;
              if (!this.organisation.country || !this.organisation.country.id) {
                this.organisation.country = new Country();
                this.organisation.country.id = null;
              } else if (this.countries) {
                this.onChangeCountry(this.organisation.country.id);
              } else {
                // The countries/languages forkJoin has not resolved yet: run it once it does.
                this.pendingCountryId = this.organisation.country.id;
              }

              if (!this.organisation.defaultLanguageGui || !this.organisation.defaultLanguageGui.id) {
                this.organisation.defaultLanguageGui = new Language();
                this.organisation.defaultLanguageGui.id = null;
              }
              if (!this.organisation.defaultLanguagePdf || !this.organisation.defaultLanguagePdf.id) {
                this.organisation.defaultLanguagePdf = new Language();
                this.organisation.defaultLanguagePdf.id = null;
              }

              if (!this.organisation.sectorOwner || !this.organisation.sectorOwner.id) {
                this.organisation.sectorOwner = new User();
                this.organisation.sectorOwner.id = null;
              }
              if (!this.organisation.negoceOwner || !this.organisation.negoceOwner.id) {
                this.organisation.negoceOwner = new User();
                this.organisation.negoceOwner.id = null;
              }
            },
            (error) => this.handleGetError(error),
          );

          // que pour édition => création : pas possible qu'il y ait des
          // managers
          this.getManagers();
          this.getAdvisors();
          this.organisationService.getFms(this.organisationId).subscribe(
            (form) => (this.fmsSettings = form.fms),
            (error) => this.handleGetError(error),
          );
        } else if (this.route.snapshot.url[1].path === 'new') {
          this.edited = false;
          this.customizationOpen = true;
          this.organisation = new Organisation();
          this.organisation.country = new Country();
          this.organisation.country.id = null;

          this.organisation.defaultLanguageGui = new Language();
          this.organisation.defaultLanguageGui.id = null;
          this.organisation.defaultLanguagePdf = new Language();
          this.organisation.defaultLanguagePdf.id = null;

          this.organisation.sectorOwner = new User();
          this.organisation.sectorOwner.id = null;
          this.organisation.negoceOwner = new User();
          this.organisation.negoceOwner.id = null;
          this.organisation.hasBeApi = false;
          this.organisation.hasI18n = false;

          // Block 4 defaults (C2027-195, PO Q39); the PO adjusts them per client afterwards.
          this.organisation.sendAfterExpertValidation = false;
          this.organisation.notifyAdvisorsOnAvailability = true;
          this.organisation.notifyManagerOnAvailability = true;
          this.organisation.wanakaBroadcastsToFarmers = false;
          this.organisation.wanakaMakesVisible = false;
          this.organisation.forcedBroadcastDelay = null;

          // Block 6 defaults (C2027-196, PO Q25): all granted by default.
          this.organisation.farmerCanCreateFarm = true;
          this.organisation.farmerCanCreateParcel = true;
          this.organisation.farmerCanEditParcel = true;
          this.organisation.farmerCanCreateOrder = true;
          this.organisation.farmerCanEditOrder = true;
          this.organisation.farmerCanPlanDelivery = true;
        }

        if (this.isOrgAdmin) {
          this.getSectorOwners();
          this.getNegoceOwners();
          if (!this.edited) {
            // Creation: preselect the creator as sector owner (was done only for ROLE_SECTOR, via
            // the now-removed getSectorOwnerMe(), before SECTOR counted as admin, C2027-194).
            this.userService.getMe().subscribe((me) => (this.organisation.sectorOwner = me));
          }
        }
        const obs1 = this.listService.getCountries(true);
        const obs2 = this.listService.getLanguages();
        forkJoin([obs1, obs2]).subscribe(
          (data: Array<any>) => {
            this.handleCountries(data[0]);
            this.handleLanguages(data[1]);
            for (const c of this.countries) {
              c.languagesGui = c.languagesGui.sort((a: any, b: any) =>
                this.languagesIndex.get(a['id']).label.localeCompare(this.languagesIndex.get(b['id']).label),
              );
              c.languagesPdf = c.languagesPdf.sort((a: any, b: any) =>
                this.languagesIndex.get(a['id']).label.localeCompare(this.languagesIndex.get(b['id']).label),
              );
            }
            if (this.pendingCountryId != null) {
              this.onChangeCountry(this.pendingCountryId);
              this.pendingCountryId = null;
            }
          },
          (error) => {
            this.handleGetError(error);
          },
        );
      }
    });
  }

  handleSaveError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('distributeur.edit.unauthorized'));
    } else if (error.status === 400 && error.error && error.error.data && error.error.data.defaultAdvisor) {
      // Block 3 (C2027-198): the back validates the default advisor belongs to the organisation.
      showErrorMessage(this.modal, this.i18nTranslator.translate('distributeur.edit.fms.advisorNotInOrganisation'));
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('edit.error.generic'));
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('distributeur.edit.view.unauthorized')).then((r) => {
        this.router.navigate(['/organisations']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.generic')).then((r) => {
        this.router.navigate(['/organisations']);
      });
    }
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.savedBlockTimeout) {
      clearTimeout(this.savedBlockTimeout);
    }
  }

  onChangeCountry(countryId: any) {
    countryId = +countryId;
    this.country = this.countries.find((c) => c.id === countryId);

    if (this.edited) {
      this.countryChanged = countryId !== this.storedCountryId;
      if (this.countryChanged) {
        this.customizationOpen = true;
      }
    }

    if (this.organisation.defaultLanguageGui) {
      let hasSameDefaultLanguageGui = false;
      for (const language of this.country.languagesGui) {
        if (language.code == this.organisation.defaultLanguageGui.code) {
          hasSameDefaultLanguageGui = true;
          break;
        }
      }
      if (!hasSameDefaultLanguageGui) {
        this.organisation.defaultLanguageGui = new Language();
        this.organisation.defaultLanguageGui.id = null;
      }
    }

    if (this.organisation.defaultLanguagePdf) {
      let hasSameDefaultLanguagePdf = false;
      for (const language of this.country.languagesPdf) {
        if (language.code == this.organisation.defaultLanguagePdf.code) {
          hasSameDefaultLanguagePdf = true;
          break;
        }
      }
      if (!hasSameDefaultLanguagePdf) {
        this.organisation.defaultLanguagePdf = new Language();
        this.organisation.defaultLanguagePdf.id = null;
      }
    }
  }
}
